import { registerPlugin } from "@capacitor/core";

/*
  Native/live location bridge — Capacitor iOS & Android
  ──────────────────────────────────────────────────────
  • window.Capacitor.Plugins.Geolocation doğrudan kullanılır
    (dynamic import iOS'ta çalışmıyor).
  • İzin akışı (iOS):
      1. checkPermissions()
      2. requestPermissions({ permissions: ["location"] })  → WhenInUse
      3. requestAlwaysAuthorization()  → Always upgrade (AppDelegate yönetir)
  • Uygulama açılışında HİÇBİR izin popup'ı çıkmaz.
  • Background watcher React state'ten BAĞIMSIZ çalışır:
      - familyId / uid / tamAd modül değişkeninde saklanır
      - Her konum güncellemesinde güvenli alan + geofence kontrolü yapılır
      - Uygulama arka planda / kapalıyken de bildirim gider
*/

/* ─── Sabitler ─── */
const MIN_WRITE_INTERVAL_MS = 10 * 1000;
const MIN_WRITE_DISTANCE_M  = 50;

/* ─── Modül-level state ─── */
let watcherId           = null;
let lastWrittenLocation = null;
let lastWrittenAt       = 0;

// Native watcher acma/kapama islemlerini tek siraya al. Capacitor callback'i
// devam ederken ikinci bir start/stop gelirse iki farkli CLLocationManager
// watcher'i veya ayni watcher icin iki remove cagrisi olusmasin.
let watcherLifecycleTail    = Promise.resolve();
let watcherLifecycleVersion = 0;
let watcherDesiredState     = "stopped";

// Background bağlam (React unmount'tan bağımsız)
let _bgFamilyId  = null;
let _bgUid       = null;
let _bgTamAd     = "";
let _bgCallbacks = null;  // { onLocation }

// Native'den (AppDelegate.swift → pushAuthStatusToJS) köprülenen GERÇEK
// CLLocationManager durumu. @capacitor/geolocation eklentisi WhenInUse ile
// Always'i AYIRT ETMİYOR (ikisi de "granted" döner) — bu yüzden Always'in
// gerçekten verilip verilmediğini doğru öğrenmenin TEK güvenilir yolu bu.
// Değerler: "authorizedAlways" | "authorizedWhenInUse" | "denied" | "notDetermined" | null (henüz bilinmiyor)
const _nativeAuthStatus = { current: null };

/* ─── Platform tespiti ─── */
const isCapacitorNative = () => {
  try {
    return !!(
      window?.Capacitor?.isNativePlatform?.() ||
      window?.Capacitor?.getPlatform?.() === "ios" ||
      window?.Capacitor?.getPlatform?.() === "android"
    );
  } catch (_) {
    return false;
  }
};

export const isNativeLocationAvailable = () => isCapacitorNative();

let pendingNativeSharingEnabled = null;
const pendingNativeLocationPreferences = new Map();

const isIosNativeBridgeTarget = () => {
  if (!isCapacitorNative()) return false;
  try { return window?.Capacitor?.getPlatform?.() === "ios"; } catch (_) { return false; }
};

const isAndroidNativeBridgeTarget = () => {
  if (!isCapacitorNative()) return false;
  try { return window?.Capacitor?.getPlatform?.() === "android"; } catch (_) { return false; }
};

const flushPendingNativeLocationBridge = () => {
  if (!isIosNativeBridgeTarget()) return false;
  const handler = window?.webkit?.messageHandlers?.aileNativeBridge;
  if (!handler?.postMessage) return false;

  if (pendingNativeSharingEnabled !== null) {
    try {
      handler.postMessage({
        action : "setLocationSharingEnabled",
        enabled: pendingNativeSharingEnabled,
      });
      pendingNativeSharingEnabled = null;
    } catch (_) {
      return false;
    }
  }

  for (const [key, value] of pendingNativeLocationPreferences) {
    try {
      handler.postMessage({ key, value });
      pendingNativeLocationPreferences.delete(key);
    } catch (_) {
      return false;
    }
  }
  return true;
};

/* ─── Plugin referansları ─── */
const getGeolocation              = () => window?.Capacitor?.Plugins?.Geolocation;
const BackgroundGeolocationPlugin = registerPlugin("BackgroundGeolocation");
const getBackgroundGeolocation    = () => BackgroundGeolocationPlugin;
const AileLocationPlugin          = registerPlugin("AileLocation");
let _nativeSharingEnabled         = null;

const nativeAuthContext = () => {
  const read = (key) => {
    try { return localStorage.getItem(key) || ""; } catch (_) { return ""; }
  };
  return {
    accessToken     : read("fp:accessToken"),
    refreshToken    : read("fp:refreshToken"),
    clientInstanceId: read("fp:authClientInstanceId"),
    apiBase         : (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE_URL)
      || "https://api.aileyonetimi.com",
  };
};

const configureAndroidNativeLocation = async ({ zones } = {}) => {
  if (!isAndroidNativeBridgeTarget()) return false;
  await AileLocationPlugin.configure({
    familyId: _bgFamilyId || "",
    uid     : _bgUid || "",
    tamAd   : _bgTamAd || "",
    ...nativeAuthContext(),
    ...(_nativeSharingEnabled === null ? {} : { enabled: _nativeSharingEnabled }),
    ...(Array.isArray(zones) ? { zones } : {}),
  });
  return true;
};

/* ─── Mesafe (metre) ─── */
const distanceMeters = (a, b) => {
  if (!a || !b) return Infinity;
  const R     = 6371000;
  const toRad = (v) => (Number(v) * Math.PI) / 180;
  const dLat  = toRad(b.lat - a.lat);
  const dLng  = toRad(b.lng - a.lng);
  const lat1  = toRad(a.lat);
  const lat2  = toRad(b.lat);
  const x     = Math.sin(dLat / 2) ** 2 +
                Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

/* ─── İzin normalize ─── */
const normalizePermission = (value) => {
  if (
    value === "granted"            ||
    value === "authorized"         ||
    value === "authorizedAlways"   ||
    value === "authorizedWhenInUse"
  ) return "granted";
  if (
    value === "prompt"                  ||
    value === "prompt-with-rationale"   ||
    value === "notDetermined"
  ) return "prompt";
  return "denied";
};

const normalizeAlwaysPermission = (value) =>
  value === "authorizedAlways" || value === "granted";

/* ─── Sadece kontrol — popup çıkarmaz ─── */
export const hasNativeLocationPermission = async () => {
  if (!isCapacitorNative()) return false;
  try {
    const Geo = getGeolocation();
    if (!Geo) return false;
    const p = await Geo.checkPermissions();
    return normalizePermission(p?.location) === "granted";
  } catch (_) {
    return false;
  }
};

/* ─── "Her Zaman İzin Ver" var mı? ───
   DÜZELTME (kritik): Eskiden bu fonksiyon @capacitor/geolocation'ın
   checkPermissions() çağrısına güveniyordu — ama bu eklenti iOS'ta
   WhenInUse ile Always'i AYIRT ETMİYOR, ikisini de "granted" döndürüyor.
   Sonuç: kullanıcı sadece WhenInUse verse bile bu fonksiyon "Always zaten
   açık" sanıyordu ve "Her Zaman İzin Ver" promptu BİR DAHA HİÇ çıkmıyordu.
   Artık önce native'den köprülenen GERÇEK durumu kullanıyoruz (bkz.
   AppDelegate.swift → pushAuthStatusToJS); bu bilgi henüz gelmediyse
   (örn. köprü daha bağlanmadıysa) eski yönteme geri düşüyoruz. */
export const hasAlwaysPermission = async () => {
  if (!isCapacitorNative()) return false;
  if (isAndroidNativeBridgeTarget()) {
    try {
      const status = await AileLocationPlugin.status();
      return status?.backgroundPermission === true;
    } catch (_) {
      return false;
    }
  }
  if (_nativeAuthStatus.current != null) {
    return _nativeAuthStatus.current === "authorizedAlways";
  }

  // @capacitor/geolocation iOS'ta WhenInUse ve Always izinlerini aynı
  // "granted" değeriyle bildirir. Native durum henüz köprülenmediyse bu genel
  // değeri Always kabul etmek, gerekli yükseltme akışını sessizce atlar ve
  // uygulama ekran kilitlenince konum güncellemeleri tamamen durur. iOS'ta
  // yalnızca AppDelegate'ten gelen açık authorizedAlways kanıtına güven.
  if (isIosNativeBridgeTarget()) return false;

  try {
    const Geo = getGeolocation();
    if (!Geo) return false;
    const p = await Geo.checkPermissions();
    return normalizeAlwaysPermission(p?.location);
  } catch (_) {
    return false;
  }
};

/* ─── "Her Zaman İzin Ver" yükseltmesi — SADECE kullanıcı eylemiyle çağrılmalı ───
   Örn: kullanıcı Aile Haritası'nda "Canlı Konum Paylaşımı"nı AÇTIĞINDA, kısa bir
   uygulama-içi açıklama onayından sonra. Sayfa açılışında veya arka planda
   OTOMATİK çağrılmamalı — App Store incelemesinde "bağlamsız Always isteği"
   olarak değerlendirilip reddedilebilir.
   Native taraf (AppDelegate) WhenInUse alınır alınmaz artık kendiliğinden
   Always istemiyor; sadece bu köprü mesajını bekliyor (bkz. AppDelegate.swift
   → NativeBridgeHandler.onRequestAlwaysLocation / requestAlwaysLocationIfNeeded). */
export const requestAlwaysLocationUpgrade = () => {
  if (!isCapacitorNative()) return;
  if (isAndroidNativeBridgeTarget()) {
    // Android 10+ arka plan konumu ayrı bir runtime iznidir. Bu çağrı yalnızca
    // Harita ekranındaki kullanıcı onayından sonra yapılır; uygulama açılışında
    // veya sessiz restore sırasında izin penceresi gösterilmez.
    AileLocationPlugin.requestBackgroundPermission().catch((e) => {
      console.warn("[Location] Android arka plan konum izni istenemedi:", e?.message || e);
    });
    return;
  }
  try {
    if (!window?.webkit?.messageHandlers?.aileNativeBridge) {
      console.warn("[Location] requestAlwaysLocationUpgrade: native köprü henüz bağlı değil.");
      return;
    }
    window.webkit.messageHandlers.aileNativeBridge.postMessage({
      action: "requestAlwaysLocation",
    });
  } catch (e) {
    console.warn("[Location] requestAlwaysLocationUpgrade hatası:", e?.message || e);
  }
};

/* ─── AppDelegate arka plan yöneticisini kullanıcı tercihiyle eşleştir ─── */
export const setNativeBackgroundLocationSharingEnabled = (enabled) => {
  if (!isCapacitorNative()) return;
  _nativeSharingEnabled = !!enabled;
  if (isAndroidNativeBridgeTarget()) {
    AileLocationPlugin.setEnabled({ enabled: _nativeSharingEnabled }).catch((e) => {
      console.warn("[Location] Android native paylaşım durumu güncellenemedi:", e?.message || e);
    });
    return;
  }
  if (!isIosNativeBridgeTarget()) return;
  pendingNativeSharingEnabled = !!enabled;
  flushPendingNativeLocationBridge();
};

const setNativeLocationPreference = (key, value) => {
  if (!isIosNativeBridgeTarget()) return;
  pendingNativeLocationPreferences.set(String(key), String(value));
  flushPendingNativeLocationBridge();
};

// WebView refresh rotasyonu tamamlandiginda iOS arka plan konum motorunun
// ayri token kopyasi eski kalmamalidir. Refresh token once yazilir; boylece
// hemen araya giren bir native location callback'i yeni access tokenla eski
// refresh token kombinasyonunu gormez. Bos degerler burada temizlenmez;
// gercek logout temizligini clearNativeBackgroundLocationSession yapar.
export const syncNativeBackgroundLocationAuth = ({ accessToken = "", refreshToken = "" } = {}) => {
  if (isAndroidNativeBridgeTarget()) {
    AileLocationPlugin.updateAuth({
      accessToken,
      refreshToken,
      ...nativeAuthContext(),
    }).catch(() => {});
    return;
  }
  if (!isIosNativeBridgeTarget()) return;
  const clientInstanceId = (() => {
    try { return localStorage.getItem("fp:authClientInstanceId") || ""; } catch { return ""; }
  })();
  if (clientInstanceId) setNativeLocationPreference("bgAuthClientInstanceId", clientInstanceId);
  if (refreshToken) setNativeLocationPreference("bgRefreshToken", refreshToken);
  if (accessToken) setNativeLocationPreference("bgFirebaseToken", accessToken);
};

/* ─── İzin iste: WhenInUse → Always upgrade ─── */
export const ensureNativePermission = async ({
  requestPermission = false,
  tryAlways         = false,
} = {}) => {
  const Geo = getGeolocation();
  if (!Geo) throw new Error("GEOLOCATION_PLUGIN_NOT_FOUND");

  let permissions = await Geo.checkPermissions();
  let status      = normalizePermission(permissions?.location);

  if (status !== "granted" && requestPermission) {
    try {
      permissions = await Geo.requestPermissions({ permissions: ["location"] });
      status      = normalizePermission(permissions?.location);
    } catch (e) {
      console.warn("[Location] requestPermissions hatası:", e);
    }
  }

  // WhenInUse alındı → Always upgrade
  // AppDelegate.swift zaten otomatik yönetiyor (CLLocationManager delegate)
  if (tryAlways && status === "granted") {
    try {
      if (typeof Geo.requestAlwaysAuthorization === "function") {
        await Geo.requestAlwaysAuthorization();
      }
      // Yoksa AppDelegate'teki CLLocationManager kendi trigger eder
    } catch (e) {
      console.warn("[Location] requestAlwaysAuthorization (kritik değil):", e);
    }
    try {
      permissions = await Geo.checkPermissions();
      status      = normalizePermission(permissions?.location);
    } catch (_) {}
  }

  if (status !== "granted") {
    const err = new Error("LOCATION_DENIED");
    err.code  = "LOCATION_DENIED";
    throw err;
  }
  return true;
};

/* ─── Web fallback ─── */
const getWebLocationOnce = ({ timeout = 12000, maximumAge = 10000 } = {}) =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const err = new Error("WEB_GEOLOCATION_UNSUPPORTED");
      err.code  = "WEB_GEOLOCATION_UNSUPPORTED";
      return reject(err);
    }
    const secure =
      window.location.protocol === "https:" ||
      window.location.hostname  === "localhost" ||
      window.location.hostname  === "127.0.0.1";
    if (!secure) {
      const err = new Error("WEB_GEOLOCATION_INSECURE");
      err.code  = "WEB_GEOLOCATION_INSECURE";
      return reject(err);
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat      : pos.coords.latitude,
        lng      : pos.coords.longitude,
        accuracy : pos.coords.accuracy  ?? null,
        speed    : pos.coords.speed     ?? null,
        heading  : pos.coords.heading   ?? null,
        timestamp: pos.timestamp        || Date.now(),
      }),
      (err) => {
        const e = new Error(err?.message || "WEB_GEOLOCATION_ERROR");
        e.code  = err?.code === 1 ? "PERMISSION_DENIED"
                : err?.code === 2 ? "POSITION_UNAVAILABLE"
                : err?.code === 3 ? "TIMEOUT"
                : "WEB_GEOLOCATION_ERROR";
        reject(e);
      },
      { enableHighAccuracy: true, timeout, maximumAge }
    );
  });

/* ─── Tek seferlik konum al ─── */
export const getCurrentLocationOnce = async ({
  requestPermission = false,
  tryAlways         = false,
  timeout           = 12000,
  maximumAge        = 10000,
} = {}) => {
  if (isCapacitorNative()) {
    await ensureNativePermission({ requestPermission, tryAlways });
    const Geo = getGeolocation();
    const pos = await Geo.getCurrentPosition({
      enableHighAccuracy: true,
      timeout,
      maximumAge,
    });
    return {
      lat      : pos.coords.latitude,
      lng      : pos.coords.longitude,
      accuracy : pos.coords.accuracy  ?? null,
      speed    : pos.coords.speed     ?? null,
      heading  : pos.coords.heading   ?? null,
      timestamp: pos.timestamp        || Date.now(),
    };
  }
  return getWebLocationOnce({ timeout, maximumAge });
};

/* ─── Yazma eşiği kontrolü ─── */
const shouldWriteLocation = (loc) => {
  const now = Date.now();
  if (!lastWrittenLocation) return true;
  const age = now - lastWrittenAt;
  // Sabit duran cihazı süre doldu diye tekrar tekrar yazma. Yalnızca en az
  // 10 saniye geçmiş ve cihaz en az 50 metre hareket etmişse gönder.
  if (age >= MIN_WRITE_INTERVAL_MS && distanceMeters(lastWrittenLocation, loc) >= MIN_WRITE_DISTANCE_M) return true;
  return false;
};

/* ══════════════════════════════════════════════════════════════
   BACKGROUND BÖLGE KONTROLÜ — KALDIRILDI (DÜZELTME)
   ──────────────────────────────────────────────────────────────
   Bu bölümde eskiden bgZoneCheck() adlı, ön plandayken her konum
   güncellemesinde güvenli alan/geofence kontrolü yapıp bildirim
   gönderen bir fonksiyon vardı. Backend (map.routes.js →
   checkZonesAndNotify) ZATEN her /location yazımında aynı kontrolü
   platformdan bağımsız (foreground/background/killed) yapıyor.
   İkisi birlikte çalışınca aynı giriş/çıkış olayı için 2 kez
   bildirim (native + push, her biri 2x) üretiliyordu. Tek doğru
   kaynak artık backend — bu yüzden kod tamamen kaldırıldı.
══════════════════════════════════════════════════════════════ */

/* ─── iOS izin değişikliği dinleyici — gerçek native durumu saklar (bkz. dosya başı _nativeAuthStatus) ─── */
if (typeof window !== "undefined" && !window.__locationAuthListenerAdded) {
  window.__locationAuthListenerAdded = true;
  window.addEventListener("aileNativeBridgeReady", () => {
    // AppDelegate köprüyü bağladığı anda, daha önce sıraya alınmış paylaşım
    // bayrağı ve kimlik/token değerlerini izin değişikliği beklemeden aktar.
    flushPendingNativeLocationBridge();
  });
  window.addEventListener("locationAuthorizationChanged", (e) => {
    _nativeAuthStatus.current = e?.detail?.status || null;
    // AppDelegate bu olayı native köprü bağlandıktan sonra gönderir. Oturum
    // başlangıcında köprü hazır olmadan verilmiş kapatma/temizleme komutları
    // burada kayıpsız biçimde tamamlanır.
    flushPendingNativeLocationBridge();
    console.log("[Location] İzin durumu değişti (native, gerçek):", _nativeAuthStatus.current);
  });
}

/* ─── Background bağlamını güncelle (React'ten çağrılır) ─── */
export const setBackgroundLocationContext = ({
  familyId,
  uid,
  tamAd             = "",
  onLocation        = null,
  zones             = null,
} = {}) => {
  _bgFamilyId         = familyId || null;
  _bgUid              = uid      || null;
  _bgTamAd            = tamAd    || "";
  // DÜZELTME (kritik): Eskiden burada `_bgCallbacks = onLocation ? {onLocation} : null;`
  // vardı — yani bu fonksiyon onLocation'sız çağrıldığında (ör. HaritaPage'deki
  // "güvenli alan/üye listesi değişti" useEffect'i, zones/uyeler güncellenince
  // sürekli tetikleniyor) önceden kurulmuş olan konum-yazma callback'ini
  // SESSİZCE SİLİYORDU. Sonuç: paylaşım açıldıktan kısa süre sonra (ilk zone/üye
  // senkronizasyonunda) watcher çalışmaya devam ediyor ama hiçbir konum backend'e
  // yazılmıyordu — harita güncellenmiyor, geofence/güvenli alan bildirimleri hiç
  // tetiklenmiyordu (backend zaten her /location yazımında otomatik kontrol ediyor,
  // ama yazım hiç gerçekleşmiyordu). Artık onLocation verilmediyse MEVCUT callback
  // korunuyor; sadece gerçekten yeni bir onLocation verildiğinde güncelleniyor.
  if (onLocation) {
    _bgCallbacks = { onLocation };
  }
  // NOT: zones/uyeler/bildirimHerkese/bildirimGoster/lang parametreleri kasıtlı
  // olarak kaldırıldı — bunlar yalnızca artık kaldırılmış olan bgZoneCheck()
  // tarafından okunuyordu (bkz. dosyanın üst kısmındaki "BACKGROUND BÖLGE
  // KONTROLÜ — KALDIRILDI" notu). HaritaPage.js bu alanları hâlâ gönderiyor
  // olabilir — zararsız, basitçe yok sayılır (JS destructuring fazla alanları
  // hata vermeden atlar).

  /*
    AppDelegate.swift native background konum için familyId ve uid'yi
    UserDefaults'a kaydet. iOS uygulama killed olunca JS çalışmaz ama
    AppDelegate CLLocationManager callback'i çalışır ve bu değerleri okur.
  */
  if (isCapacitorNative() && familyId && uid) {
    if (isAndroidNativeBridgeTarget()) {
      configureAndroidNativeLocation({ zones }).catch((e) => {
        console.warn("[Location] Android native bağlamı güncellenemedi:", e?.message || e);
      });
      return;
    }
    // @capacitor/preferences gerekmez — WKScriptMessageHandler köprüsü kullanılır:
    // AppDelegate'teki WKScriptMessageHandler "aileNativeBridge" dinler
    setNativeLocationPreference("bgFamilyId", familyId);
    setNativeLocationPreference("bgUid",      uid);
    setNativeLocationPreference("bgTamAd",    tamAd || "");

    // JWT ve refresh token AppDelegate köprüsü üzerinden Keychain'e; token
    // dışındaki konum bağlamı UserDefaults'a yazılır.
    // Uygulama killed/background durumunda AppDelegate CLLocationManager callback'i
    // bu token'ı okuyarak backend /map/{familyId}/location endpoint'ine yazar.
    // apiClient'tan import etmek circular dependency yaratır; doğrudan localStorage oku.
    try {
      const jwt = (() => { try { return localStorage.getItem("fp:accessToken") || ""; } catch { return ""; } })();
      const rf  = (() => { try { return localStorage.getItem("fp:refreshToken") || ""; } catch { return ""; } })();
      const clientInstanceId = (() => { try { return localStorage.getItem("fp:authClientInstanceId") || ""; } catch { return ""; } })();
      const apiBase = (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE_URL)
        || "https://api.aileyonetimi.com";
      if (jwt)  setNativeLocationPreference("bgFirebaseToken", jwt);
      if (rf)   setNativeLocationPreference("bgRefreshToken",  rf);
      if (clientInstanceId) setNativeLocationPreference("bgAuthClientInstanceId", clientInstanceId);
      setNativeLocationPreference("bgApiBase", apiBase);
    } catch (_) {}

    if (Array.isArray(zones)) {
      try {
        window?.webkit?.messageHandlers?.aileNativeBridge?.postMessage({
          action: "syncLocationRegions",
          familyId,
          uid,
          zones,
        });
      } catch (_) {}
    }
  }
};

const watcherLifecycleSirala = (operation) => {
  const queued = watcherLifecycleTail.then(operation, operation);
  // Bir native hata sonraki start/stop islemlerinin siraya alinmasini engellemesin.
  watcherLifecycleTail = queued.catch(() => {});
  return queued;
};

const watcherKaldir = async (id) => {
  if (id === null || id === undefined) return;
  const BG = getBackgroundGeolocation();
  await BG.removeWatcher({ id });
};

/* ─── Arka plan izlemeyi durdur ─── */
export const stopNativeLocationSharing = () => {
  watcherDesiredState = "stopped";
  watcherLifecycleVersion += 1;

  if (isAndroidNativeBridgeTarget()) {
    watcherId = null;
    return AileLocationPlugin.setEnabled({ enabled: false }).catch((err) => {
      console.warn("[Location] Android native servis durdurulamadı:", err?.message || err);
    });
  }

  return watcherLifecycleSirala(async () => {
    const kaldirilacakId = watcherId;
    // Kimligi await'ten once bosalt: ayni anda gelen ikinci stop ayni native
    // watcher'i tekrar kaldirmaya calismaz. Hata olursa kontrollu retry icin
    // kimlik asagida geri yuklenir.
    watcherId = null;

    try {
      await watcherKaldir(kaldirilacakId);
    } catch (err) {
      if (watcherId === null) {
        watcherId = kaldirilacakId;
      }
      console.warn("[Location] Watcher kaldırılamadı:", err);
    }
  });
};

/* ─── Watcher gerçekten çalışıyor mu? ───
   Uygulama yeniden açıldığında global oturum yöneticisi, Harita sayfasının
   daha önce mount edilip edilmediğine güvenmeden native watcher'ı restore
   eder. Aktif watcher varsa yeniden kurmayarak gereksiz lifecycle yarışı ve
   kısa süreli konum boşluğu oluşmasını engeller. */
export const isNativeLocationSharingRunning = () => (
  watcherDesiredState === "started" && watcherId !== null
);

/* ─── Oturum sonlandığında native konum bağlamını tamamen temizle ─── */
export const clearNativeBackgroundLocationSession = () => {
  // AppDelegate bayrağı ve kimlik/token bağlamı watcher kaldırılmadan önce
  // temizlenir. Böylece gecikmiş bir native callback eski kullanıcı adına
  // backend yazımı başlatamaz.
  setNativeBackgroundLocationSharingEnabled(false);
  [
    "bgFamilyId",
    "bgUid",
    "bgTamAd",
    "bgFirebaseToken",
    "bgRefreshToken",
    "bgAuthClientInstanceId",
  ].forEach((key) => setNativeLocationPreference(key, ""));

  _bgFamilyId = null;
  _bgUid = null;
  _bgTamAd = "";
  _bgCallbacks = null;
  _nativeSharingEnabled = false;
  lastWrittenLocation = null;
  lastWrittenAt = 0;

  if (isAndroidNativeBridgeTarget()) {
    watcherDesiredState = "stopped";
    watcherLifecycleVersion += 1;
    watcherId = null;
    return AileLocationPlugin.clear().catch((err) => {
      console.warn("[Location] Android native konum oturumu temizlenemedi:", err?.message || err);
    });
  }

  return stopNativeLocationSharing();
};

/* ─── Arka plan izlemeyi başlat ─── */
export const startNativeLocationSharing = async ({
  onLocation,
  requestPermission = false,
  tryAlways         = false,
} = {}) => {
  if (!isCapacitorNative()) return null;

  watcherDesiredState = "started";
  const requestVersion = ++watcherLifecycleVersion;

  if (isAndroidNativeBridgeTarget()) {
    await ensureNativePermission({ requestPermission, tryAlways: false });
    if (watcherDesiredState !== "started" || watcherLifecycleVersion !== requestVersion) return null;
    _nativeSharingEnabled = true;
    await configureAndroidNativeLocation();
    watcherId = "aile-native-android";
    return watcherId;
  }

  return watcherLifecycleSirala(async () => {
    const halaBaslatilmali = () => (
      watcherDesiredState === "started" && watcherLifecycleVersion === requestVersion
    );

    if (!halaBaslatilmali()) return null;
    await ensureNativePermission({ requestPermission, tryAlways });
    if (!halaBaslatilmali()) return null;

    const oncekiWatcherId = watcherId;
    watcherId = null;
    if (oncekiWatcherId !== null) {
      try {
        await watcherKaldir(oncekiWatcherId);
      } catch (err) {
        watcherId = oncekiWatcherId;
        console.warn("[Location] Onceki watcher kaldırılamadı:", err);
        throw err;
      }
    }
    if (!halaBaslatilmali()) return null;

    if (onLocation) {
      _bgCallbacks = { onLocation };
    }

    // Ağ yavaşken callback'ler üst üste gelirse her noktayı ayrı ayrı sunucuya
    // yığma. Devam eden yazım bitene kadar yalnızca en yeni konumu tut.
    let writeInFlight = false;
    let queuedLatestLocation = null;
    const flushLatestLocation = async () => {
      if (writeInFlight || !queuedLatestLocation || !halaBaslatilmali()) return;

      const loc = queuedLatestLocation;
      queuedLatestLocation = null;
      if (!shouldWriteLocation(loc)) return;

      const cbs = _bgCallbacks;
      if (!cbs?.onLocation) return;

      writeInFlight = true;
      try {
        await cbs.onLocation({ ...loc, shouldSaveHistory: true });
        if (halaBaslatilmali()) {
          lastWrittenLocation = loc;
          lastWrittenAt = Date.now();
        }
      } catch (err) {
        console.warn("[Location] Konum backend'e yazılamadı:", err);
      } finally {
        writeInFlight = false;
        if (queuedLatestLocation && halaBaslatilmali()) {
          void flushLatestLocation();
        }
      }
    };

    const BG = getBackgroundGeolocation();
    const yeniWatcherId = await BG.addWatcher(
      {
        backgroundMessage  : "Canlı konum paylaşımı açık.",
        backgroundTitle    : "Aile Yönetimi",
        requestPermissions : !!requestPermission,
        stale              : false,
        distanceFilter     : 50,
      },
      async (location, error) => {
        // Stop istendigi anda native callback henuz kuyrukta olsa bile yeni bir
        // backend yazimi baslatma. requestVersion eski watcher callback'lerini de
        // yeni oturumdan ayirir.
        if (!halaBaslatilmali()) return;
        if (error) {
          console.warn("[Location] Background hatası:", error);
          return;
        }
        if (!location?.latitude || !location?.longitude) return;

        const loc = {
          lat      : location.latitude,
          lng      : location.longitude,
          accuracy : location.accuracy                    ?? null,
          speed    : location.speed                       ?? null,
          heading  : location.bearing ?? location.heading ?? null,
          timestamp: location.time                        || Date.now(),
        };

        queuedLatestLocation = loc;
        await flushLatestLocation();

        // DÜZELTME (kritik — çift bildirim hatası): Eskiden burada, uygulama ön
        // plandayken AYRICA bgZoneCheck(loc) de çağrılıyordu. Ama backend
        // (map.routes.js → checkZonesAndNotify) ZATEN her /location yazımında
        // OTOMATİK olarak aynı güvenli alan/geofence kontrolünü yapıyor —
        // platformdan bağımsız, foreground/background/killed farketmeksizin.
        // İkisi birden çalışınca AYNI giriş/çıkış olayı için 2 ayrı bildirim
        // (native + push, her biri 2 kez) üretiliyordu. bgZoneCheck() ve
        // yardımcıları tamamen kaldırıldı — kontrol artık SADECE backend'de.
      }
    );

    if (!halaBaslatilmali()) {
      try {
        await watcherKaldir(yeniWatcherId);
      } catch (err) {
        watcherId = yeniWatcherId;
        console.warn("[Location] Iptal edilen watcher kaldırılamadı:", err);
      }
      return null;
    }

    watcherId = yeniWatcherId;
    return watcherId;
  });
};
